import type { Cart, CartRequest } from 'models/api';
import type { DiscountCodeNumber } from 'types';
import { filterObject } from 'utils/collection';
import { is } from 'utils/helpers';

/**
 * Helper function to extract existing discount codes and merge old and new
 * ones, remove ones existing in both existing and incoming, add incoming ones.
 */
function getDiscountCodes(
	cart: Cart,
	updateCartProperties?: UpdatableCartProperties,
): DiscountCodeNumber[] {
	let existingCodes = [
		cart.freightDiscount?.checkNumber,
		cart.campaignDiscount?.checkNumber,
		...(cart.bonusDiscounts?.map((b) => b.checkNumber) ?? []),
	];

	[
		updateCartProperties?.cartDiscountCode,
		updateCartProperties?.bonusChequeId,
	].forEach((checkNum) => {
		existingCodes = existingCodes.includes(checkNum)
			? existingCodes.filter((checkNr) => checkNr !== checkNum)
			: [...existingCodes, checkNum];
	});

	return existingCodes.filter(is.truthy);
}

export interface UpdatableCartProperties extends Partial<
	Pick<
		Cart,
		| 'allowAnonymousReviews'
		| 'alternativeDeliveryAddress'
		| 'companyIdentificationNumber'
		| 'customerAddress'
		| 'deliveryAddress'
		| 'email'
		| 'freeReferenceText'
		| 'giftCards'
		| 'selectedContactReference'
		| 'selectedDelivery'
		| 'selectedPayment'
		| 'telephoneNumber'
		| 'vatRegistrationNumber'
	>
> {
	// A bonusCheque added or removed in checkout
	bonusChequeId?: DiscountCodeNumber;
	// A freight, campaign discount code added in the cart
	cartDiscountCode?: DiscountCodeNumber;
}

export type CheckoutUpdatableCartProperties = Omit<
	UpdatableCartProperties,
	'cartDiscountCode'
>;

export type CartUpdatableCartProperties = Pick<
	UpdatableCartProperties,
	'cartDiscountCode'
>;

/** Returns a Cart object formatted for request (removes unnecessary attributes). */
export function formatCartForRequest(
	cart: Cart,
	updateCartProperties?: UpdatableCartProperties,
): CartRequest {
	return filterObject(
		{
			variants: cart.basketRows?.map((row) => ({
				variantId: row.variantId,
				quantity: row.quantity,
				customizationId: row.customization?.customization?.id,
				metaData: row.metaData,
			})),
			selectedContactReferenceId:
				updateCartProperties?.selectedContactReference?.id ??
				cart.selectedContactReference?.id,
			giftCards: updateCartProperties?.giftCards ?? cart.giftCards,
			selectedDelivery:
				updateCartProperties?.selectedDelivery ?? cart.selectedDelivery,
			selectedPayment:
				updateCartProperties?.selectedPayment ?? cart.selectedPayment,
			email: updateCartProperties?.email ?? cart.email,
			useAlternativeDeliveryAddress:
				updateCartProperties?.alternativeDeliveryAddress ??
				cart.alternativeDeliveryAddress,
			telephoneNumber:
				updateCartProperties?.telephoneNumber ?? cart.telephoneNumber,
			deliveryAddress:
				updateCartProperties?.deliveryAddress ?? cart.deliveryAddress,
			customerAddress:
				updateCartProperties?.customerAddress ?? cart.customerAddress,
			companyIdentificationNumber:
				updateCartProperties?.companyIdentificationNumber ??
				cart.companyIdentificationNumber,
			vatRegistrationNumber:
				updateCartProperties?.vatRegistrationNumber ??
				cart.vatRegistrationNumber,
			freeReferenceText:
				updateCartProperties?.freeReferenceText ?? cart.freeReferenceText,
			discountCodes: getDiscountCodes(cart, updateCartProperties),
			allowAnonymousReviews:
				updateCartProperties?.allowAnonymousReviews ??
				cart.allowAnonymousReviews,
		},
		(val) => val !== undefined,
	);
}
